import { useMemo } from "react";
import { useLocale } from "react-aria";
import { DateFormatter } from "@internationalized/date";

import { ServerStateManager } from "~/modules/StateManager.server";

interface UseLocalisedPeriod {
  start: string | null;
  end: string | null;
  timeZone: string | null;
}

export function useLocalisedDate({
  dateStyle,
  start,
  timeZone,
}: Pick<UseLocalisedPeriod, "start" | "timeZone"> & Pick<Intl.DateTimeFormatOptions, "dateStyle">) {
  const { locale } = useLocale();

  const date = useMemo(() => {
    if (!start || !timeZone) return null;

    const startDate = new Date(start);

    const formatter = new DateFormatter(locale, { dateStyle: dateStyle ?? "full", timeZone });

    return formatter.format(startDate);
  }, [start, locale, timeZone, dateStyle]);

  return date;
}

export function useLocalisedMonth({
  start,
  timeZone,
}: Pick<UseLocalisedPeriod, "start" | "timeZone">) {
  const { locale } = useLocale();

  const date = useMemo(() => {
    if (!start || !timeZone) return null;

    const startDate = new Date(start);

    const formatter = new DateFormatter(locale, { month: "short", timeZone });

    return formatter.format(startDate);
  }, [start, locale, timeZone]);

  return date;
}

export function useLocalisedTime({ end, start, timeZone }: UseLocalisedPeriod) {
  const { locale } = useLocale();

  const time = useMemo(() => {
    if (!start || !end || !timeZone) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);

    const formatter = new DateFormatter(locale, { timeStyle: "short", timeZone });

    return { start: formatter.format(startDate), end: formatter.format(endDate) };
  }, [start, end, locale, timeZone]);

  return time;
}

export function useLocalisedTimePeriod({ end, start, timeZone }: UseLocalisedPeriod) {
  const period = useLocalisedTime({ end, start, timeZone });
  if (!period) {
    return null;
  }

  return `${period.start} - ${period.end}`;
}

// export function setTimetableOverlappingSlotError(state: ServerStateManager): ServerStateManager;
// export function setTimetableOverlappingSlotError(state: StateManager): StateManager;
export function setTimetableOverlappingSlotError(state: ServerStateManager) {
  // const state
  return state.setState((draft) => {
    delete draft.timetable.start;
    delete draft.timetable.end;
    delete draft.timetable.timeZone;
    draft.timetable.employeeIds = [];
    delete draft.recaptcha;

    draft.errors.timetable = "true";
  });
}
